import { css, type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import ArrowDown from '@engineering/icons/arrow-down';
import ArrowLast from '@engineering/icons/arrow-last';
import ArrowNext from '@engineering/icons/arrow-next';
import ArrowUp from '@engineering/icons/arrow-up';
import Close from '@engineering/icons/close';
import { TooltipBadge } from '@silvertours/front-legacy-entities';
import { Heading1 } from '@silvertours/front-shared';

import { SearchInput } from './SearchInput';
import {
  getDefaultGridTemplateAreas,
  getDefaultWideGridTemplateAreas,
  getExtraLargeGridTemplateAreas,
  getLargeGridTemplateAreas,
  getMediumGridTemplateAreas,
  getThinGridTemplateAreas,
} from './SearchFormLayout.styles';

type AffiliateProps = {
  $customColor?: string;
};

type ErrorPageProps = {
  $isErrorPage?: boolean;
};

type WideProps = {
  $isWide?: boolean;
  $isStageVisibility?: boolean;
};

type ThinProps = {
  $isThin?: boolean;
};

const Wrapper = styled.section<
  WideProps & ThinProps & { $isModernStage?: boolean }
>`
  position: ${({ $isWide }) => ($isWide ? '' : 'relative')};
  background: ${({ $isWide }) => ($isWide ? '' : 'white')};
  width: ${({ $isWide, $isThin }) => ($isWide || $isThin ? '100%' : '')};
  ${({ $isWide, $isThin, $isModernStage }) => {
    if ($isThin) return 'margin: 0; max-width: none;';
    if ($isWide)
      return `margin: ${$isModernStage ? '0' : '1.875rem'} auto 1.875rem; max-width: 704px;`;
    return `margin: ${$isModernStage ? '0' : '1.875rem'} 0 1.875rem;`;
  }}

  ${({ theme }) => theme.mq('sm')} {
    ${({ $isWide, $isModernStage }) =>
      $isWide && $isModernStage && 'padding-top: 4.5rem;'}
  }

  ${({ theme }) => theme.mq('md')} {
    ${({ $isThin, $isWide, $isModernStage }) => {
      if ($isThin) return '';
      if ($isWide && $isModernStage)
        return 'margin-top: 0; margin-bottom: 0.625rem; padding-inline: 0.9375rem; padding-bottom: 0.625rem;';
      if ($isWide)
        return 'margin-block: 0.625rem; padding: 0.625rem 0.9375rem;';
      return 'margin-block: 0.625rem; margin-right: 0.625rem; padding: 0.625rem 0.9375rem;';
    }}
  }

  ${({ theme }) => theme.mq('lg')} {
    ${({ $isThin, $isWide, $isStageVisibility, $isModernStage }) => {
      if ($isStageVisibility && !$isThin)
        return 'margin-block: 1rem; padding-inline: 0; max-width: none; margin-bottom: -7rem; z-index: 1;';
      if ($isThin) return 'margin-block: 0;';
      if ($isWide && $isModernStage)
        return 'margin-top: 0; margin-bottom: 1rem; padding-top: 0; padding-inline: 0; max-width: none;';
      if ($isWide)
        return 'margin-block: 1rem; padding-inline: 0; max-width: none;';
      return 'margin-block: 1rem; margin-right: 1rem;';
    }}
  }
`;

const PageHeadline = styled(Heading1)`
  display: none;
  color: ${({ theme }) => theme.colors.brand.green100};
  padding: 0;
  margin: 0;

  ${({ theme }) => theme.mq('sm')} {
    display: block;
    font-size: 26px;
    line-height: 2rem;
    padding-bottom: 1rem;
    padding-top: 0.5rem;
  }

  ${({ theme }) => theme.mq('md')} {
    padding-top: 0;
  }

  ${({ theme }) => theme.mq('lg')} {
    font-size: 2rem;
    line-height: 38px;
  }
`;

const Title = styled.strong<AffiliateProps & WideProps>`
  display: none;
  padding: ${({ $isWide }) => ($isWide ? '0 0 1.5rem' : '0.75rem 0 0.5rem')};
  font-family: ${({ theme }) => theme.fontFamily.alternate};
  font-size: ${({ $isWide }) => ($isWide ? '1.875rem' : '1.375rem')};
  font-weight: 400;
  line-height: 1.125;
  color: ${({ theme, $isWide }) =>
    $isWide ? theme.colors.neutral.white100 : ''};

  ${({ theme, $isWide }) => !$isWide && theme.mq('sm')} {
    max-width: 18.5rem;
  }

  ${({ theme, $isWide }) => $isWide && theme.mq('lg')} {
    font-size: 3rem;
  }

  ${({ $customColor }) =>
    $customColor &&
    css`
      padding-bottom: 1rem;
      color: ${$customColor};
      font-weight: 700;
    `}
`;

const Subtitle = styled.p<WideProps>`
  margin: ${({ $isWide }) => ($isWide ? '0 0 1rem' : '0 0 0.625rem')};
  color: ${({ theme, $isWide }) =>
    $isWide ? theme.colors.brand.green120 : theme.colors.neutral.grey160};
  font-family: ${({ theme, $isWide }) =>
    $isWide ? theme.fontFamily.alternate : ''};
  font-size: ${({ $isWide }) => ($isWide ? '1.375rem' : '1rem')};
  line-height: ${({ $isWide }) => ($isWide ? '1.625rem' : '')};
  text-wrap: pretty;

  ${({ theme, $isWide }) => $isWide && theme.mq('lg')} {
    font-size: 1.5rem;
    line-height: 1.25;
  }
`;

type FormProps2 = WideProps &
  ErrorPageProps &
  ThinProps & { $isModernStage?: boolean };

const StyledForm = styled.form<FormProps2>`
  position: ${({ $isWide }) => $isWide && 'relative'};
  display: ${({ $isWide }) => !$isWide && 'grid'};
  grid-template-areas: ${({ $isWide }) =>
    !$isWide && "'subtitle subtitle' 'form options'"};
  padding: ${({ $isWide, $isThin }) => {
    if ($isThin) return '1rem 2rem';
    if ($isWide) return '1.5rem 2rem';
    return '';
  }};
  border-radius: ${({ theme, $isWide }) => $isWide && theme.radius.md};
  border: ${({ $isErrorPage, $isModernStage }) =>
    ($isErrorPage || $isModernStage) && '1px solid'};
  border-color: ${({ theme, $isErrorPage, $isModernStage }) => {
    if ($isModernStage) return theme.colors.neutral.grey40;
    if ($isErrorPage) return theme.colors.neutral.grey80;
    return undefined;
  }};
  box-shadow: ${({ $isErrorPage, $isModernStage }) => {
    if ($isModernStage) return '0 4px 14px rgba(0,0,0,0.12)';
    if ($isErrorPage) return '0 4px 28px 0 #0000001F';
    return undefined;
  }};
  background-color: ${({ theme, $isWide }) =>
    $isWide && theme.colors.neutral.white100};
`;

const StyledBadge = styled(TooltipBadge)<WideProps>`
  top: ${({ $isWide }) => $isWide && '-0.5rem'};
  right: ${({ $isWide }) => $isWide && '-1rem'};

  ${({ theme, $isWide }) => $isWide && theme.mq('md')} {
    top: -1rem;
    right: 2rem;
  }

  background-color: ${({ theme }) => theme.colors.brand.orange80};
  background-image: none;
`;

const DepartureInput = styled(SearchInput)`
  grid-area: departure;
`;

const DestinationInput = styled(SearchInput)`
  grid-area: destination;
`;

const ErrorMessage = styled.div<WideProps>`
  grid-area: location-error;
  margin: ${({ $isWide }) =>
    $isWide ? '-0.5rem 1rem 1rem' : '-0.125rem 1rem 1rem'};
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.accent.red100};
`;

const LocationErrorMessage = styled(ErrorMessage)<WideProps>`
  grid-area: location-error;
`;

const DateErrorMessage = styled(ErrorMessage)<WideProps>`
  grid-area: date-error;
`;

type FormProps = {
  $hasDestinationInput: boolean;
  $hasMoreOptionsToggle: boolean;
  $hasLocationError: boolean;
  $hasDateError: boolean;
  $isThin?: boolean;
  $hasSecondRow?: boolean;
};

const FormContainer = styled.div<WideProps & FormProps>`
  grid-area: form;
  display: grid;
  grid-template-areas: ${({ $isWide, $isThin, ...props }) => {
    if ($isThin) {
      return getThinGridTemplateAreas(props);
    }
    return $isWide
      ? getDefaultWideGridTemplateAreas(props)
      : getDefaultGridTemplateAreas(props);
  }};
  grid-template-columns: ${({ $isWide, $isThin }) => {
    if ($isThin) {
      // always two 1fr columns — departure spans both when destination is absent
      // keeping column widths stable prevents age field from jumping on toggle
      return '1fr 1fr auto auto auto auto';
    }
    return $isWide ? 'repeat(2, 1fr)' : '1fr';
  }};
  gap: ${({ $isWide, $isThin, $hasSecondRow }) => {
    if ($isThin) {
      return $hasSecondRow ? '1rem 1rem' : '0 1rem';
    }
    return $isWide ? '1rem' : '0.625rem';
  }};
  width: ${({ $isWide }) => ($isWide ? '' : '505px')};

  ${({ theme, $isWide, $isThin, ...props }) =>
    $isWide &&
    !$isThin &&
    css`
      /* edge case for small tablets */
      @media (min-width: 634px) {
        grid-template-areas: ${getMediumGridTemplateAreas(props)};
      }

      ${theme.mq('lg')} {
        grid-template-areas: ${getLargeGridTemplateAreas(props)};
        grid-template-columns: repeat(4, 1fr);
      }

      /* edge case for slightly larger screens */
      @media (min-width: 1080px) {
        grid-template-areas: ${getExtraLargeGridTemplateAreas(props)};
      }

      /* error style overrides for wide layout */
      [role='alert'] {
        margin-bottom: 0;
      }
    `}
  ${({ $isThin }) => $isThin && 'max-width: 1230px; margin: 0 auto;'}
`;

const ButtonWrapper = styled.div<AffiliateProps & ThinProps>`
  grid-area: button;

  ${({ $isThin }) =>
    $isThin &&
    css`
      button {
        height: 50px;
      }
    `}

  button {
    ${({ $customColor }) =>
      $customColor &&
      css`
        background-color: ${$customColor};
      `}
  }
`;

// Second row in thin mode — flex container outside the grid
const ThinSecondRow = styled.div`
  grid-area: second-row;
  display: flex;
  align-items: center;
  gap: 1rem;

  div {
    padding-top: 0;
  }
`;

// Wrapper for the more/less toggle with vertical padding
const ThinToggleWrapper = styled.div`
  grid-area: options-toggle;
  padding: 1rem;
  width: 97px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const ThinToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 1rem;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.neutral.grey160};
`;

const ThinToggleArrowDown = styled(ArrowDown)`
  height: 1.125rem;
`;

const ThinToggleArrowUp = styled(ArrowUp)`
  height: 1.125rem;
`;

type FooterLinkProps = {
  iconOnLeft?: boolean;
};

const FooterLink = styled.div<FooterLinkProps>`
  grid-area: options-toggle;
  text-align: right;
  margin-bottom: 0;

  a {
    ${({ iconOnLeft }) => iconOnLeft && 'flex-direction: row-reverse;'}
  }
`;

const getOptionsStyles = (theme: Theme) => css`
  width: 0.75rem;
  height: 0.75rem;
  fill: ${theme.colors.brand.green120};
`;

const OptionsOpenIcon = styled(ArrowLast)`
  ${({ theme }) => getOptionsStyles(theme)};
`;

const OptionsCloseIcon = styled(ArrowNext)`
  ${({ theme }) => getOptionsStyles(theme)};
`;

const CloseIcon = styled(Close)`
  cursor: pointer;
`;

const TryAgainButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
`;

export {
  ButtonWrapper,
  CloseIcon,
  DepartureInput,
  DestinationInput,
  FooterLink,
  FormContainer,
  OptionsCloseIcon,
  OptionsOpenIcon,
  PageHeadline,
  StyledBadge,
  StyledForm,
  Subtitle,
  ThinSecondRow,
  ThinToggleWrapper,
  ThinToggleButton,
  ThinToggleArrowDown,
  ThinToggleArrowUp,
  Title,
  Wrapper,
  LocationErrorMessage,
  DateErrorMessage,
  TryAgainButtonWrapper,
};
